import React, { useEffect, useRef, useState } from 'react';
import { useCanvas } from './hooks/useCanvas';
import { useShapeDrawing } from './hooks/useShapeDrawing';
import { useLayerManagement } from './hooks/useLayerManagement';
import { useImageHandling } from './hooks/useImageHandling';
import { calculateBaseScale, isPointInShape } from './utils/shapeUtils';

// Import components
import LayersPanel from './components/LayersPanel';
import DrawingTools from './components/DrawingTools';
import ReuploadControl from './components/ReuploadControl';
import ResizeControls from './components/ResizeControls';
import UploadPrompt from './components/UploadPrompt';
import ExportModal from './components/ExportModal';

// Add at the top with other imports
import defaultMap from '../../assets/map1.jpg';  // You'll need to add this image

// First, let's check if the import worked
console.log('Imported defaultMap:', defaultMap);

const CanvasMap = React.forwardRef(({ 
    width = '50vw', 
    height = '100%', 
    isEditMode = false, 
    showResizeControls = false, 
    showReuploadControls = false,
    onCanvasReady,
    onContentChange,
    initialContent,
    initialImage,
    setHasUploadedMap,
    onExport
}, ref) => {
    // 1. Initialize shape drawing first
    const {
        currentShape,
        selectedTool,
        handleDrawStart,
        handleDrawMove,
        handleDrawEnd,
        handleResizeStart,
        clearDrawingState,
        setSelectedTool,
        handleShapeEdit,
        isResizing,
        isDragging,
        resizeHandle,
        setIsResizing,
        setIsDragging,
        setResizeHandle,
        ...drawingOps
    } = useShapeDrawing();

    // 2. Initialize layer management
    const {
        layers,
        selectedLayer,
        editingLayer,
        isLayersPanelCollapsed,
        addImageLayer,
        addShapeLayer,
        updateLayerData,
        renameLayer,
        updateLayerColor,
        ...layerOps
    } = useLayerManagement(setSelectedTool);

    // 3. Initialize image handling
    const {
        image,
        scale,
        position,
        isDragging: imageDragging,
        isDragOver,
        fileInputRef,
        handleImageUpload,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleZoom,
        handleReset,
        handleImageDragStart,
        handleImageDrag,
        handleImageDragEnd,
        isResizeDragging
    } = useImageHandling(addImageLayer, setHasUploadedMap);

    // 4. Initialize canvas last since it needs all the other values
    const { canvasRef, redrawCanvas } = useCanvas(
        image, 
        layers, 
        currentShape, 
        position, 
        scale,
        editingLayer
    );

    // 5. Update useShapeDrawing with redrawCanvas using useEffect
    useEffect(() => {
        if (redrawCanvas) {
            drawingOps.setRedrawCanvas?.(redrawCanvas);
        }
    }, [redrawCanvas]);

    // Add a ref to track if mouse is down
    const isMouseDown = useRef(false);

    // Add new state at the top with other states
    const [editingText, setEditingText] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [anonymousUserId, setAnonymousUserId] = useState('');

    // Add text editing handlers
    const handleTextEdit = (layerId) => {
        setEditingText(layerId);
    };

    const handleTextChange = (e, layerId) => {
        updateLayerData(layerId, (data) => ({
            ...data,
            content: e.target.value
        }));
    };

    // Mouse event handlers
    const handleMouseDown = (e) => {
        if (!canvasRef.current) return;
        
        const rect = canvasRef.current.getBoundingClientRect();
        const imageLayer = layers.find(layer => layer.type === 'image');
        const baseScale = calculateBaseScale(rect, imageLayer);
        const finalScale = baseScale * scale;

        // Handle double-click for textbox editing
        if (e.detail === 2 && isEditMode) {  // Check for double-click
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const clickX = (e.clientX - rect.left - centerX - position.x) / finalScale;
            const clickY = (e.clientY - rect.top - centerY - position.y) / finalScale;

            // Check for textbox layers from top to bottom
            for (let i = layers.length - 1; i >= 0; i--) {
                const layer = layers[i];
                if (layer?.type === 'shape' && layer.data.type === 'textbox' && layer.visible) {
                    const shape = layer.data;
                    // Check if click is within textbox bounds
                    if (clickX >= shape.startX && 
                        clickX <= shape.startX + shape.width &&
                        clickY >= shape.startY && 
                        clickY <= shape.startY + shape.height) {
                        handleTextEdit(layer.id);
                        e.stopPropagation();
                        return;
                    }
                }
            }
        }
        
        isMouseDown.current = true;
        
        // Handle resize/drag for editing layer
        if (editingLayer && isEditMode) {
            const layer = layers.find(l => l.id === editingLayer);
            console.log('Attempting to edit layer:', layer);
            if (layer && layer.type === 'shape') {
                if (handleResizeStart(e, layer.data, finalScale, rect, rect.width/2, rect.height/2, position)) {
                    e.stopPropagation();
                    return;
                }
            }
        }

        // Handle shape selection when clicking on canvas
        if (selectedTool === 'pointer' && isEditMode) {
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const clickX = (e.clientX - rect.left - centerX - position.x) / finalScale;
            const clickY = (e.clientY - rect.top - centerY - position.y) / finalScale;

            // Check shapes from top to bottom
            for (let i = layers.length - 1; i >= 0; i--) {
                const layer = layers[i];
                if (layer?.type === 'shape' && layer.visible) {
                    if (isPointInShape(clickX, clickY, layer.data, finalScale)) {
                        layerOps.handleLayerClick(layer.id);
                        e.stopPropagation();
                        return;
                    }
                }
            }
            // If no shape was clicked, clear selection
            layerOps.setSelectedLayer(null);
            layerOps.setEditingLayer(null);
        }

        // Handle image dragging - ONLY if in resize mode
        if (showResizeControls && !isEditMode) {
            handleImageDragStart(e);
            e.stopPropagation();
            return;
        }

        // Handle shape drawing
        if (isEditMode && selectedTool !== 'pointer') {
            const newShape = handleDrawStart(e, rect, position, baseScale, scale);
            if (newShape) {
                // If handleDrawStart returns a shape (for textbox), add it immediately
                addShapeLayer(newShape);
            }
        }
    };

    const handleMouseMove = (e) => {
        if (!canvasRef.current || !isMouseDown.current) return;
        
        const rect = canvasRef.current.getBoundingClientRect();
        const imageLayer = layers.find(layer => layer.type === 'image');
        const baseScale = calculateBaseScale(rect, imageLayer);
        const finalScale = baseScale * scale;
        
        // Handle shape editing
        if (editingLayer) {
            console.log('Editing layer:', editingLayer);
            console.log('Drawing ops state:', {
                isResizing,
                isDragging,
                resizeHandle
            });
            
            if (isResizing || isDragging) {
                console.log('Moving/resizing shape:', isResizing ? 'resize' : 'drag');
                handleShapeEdit(e, rect, position, baseScale, scale, editingLayer, updateLayerData);
                redrawCanvas();
                return;
            }
        }

        // Handle image dragging - ONLY if in resize mode
        if (imageDragging && showResizeControls && !isEditMode) {
            handleImageDrag(e);
            redrawCanvas();
            return;
        }

        if (currentShape) {
            handleDrawMove(e, rect, position, baseScale, scale);
            return;
        }
    };

    const handleMouseUp = () => {
        isMouseDown.current = false;  // Reset mouse down state
        
        if (isDragging) {
            if (!editingLayer) {
                handleImageDragEnd();
            }
            setIsDragging(false);
        } else if (isResizing) {
            setIsResizing(false);
            setResizeHandle(null);
        } else if (currentShape) {
            const newShape = handleDrawEnd();
            if (newShape) {
                addShapeLayer(newShape);
            }
        }
        clearDrawingState();
    };

    const handleMouseLeave = () => {
        isMouseDown.current = false;
        if (isDragging) {
            setIsDragging(false);
        }
        if (isResizing) {
            setIsResizing(false);
            setResizeHandle(null);
        }
        handleMouseUp();
    };

    // Add useEffect to watch isEditMode changes
    useEffect(() => {
        if (!isEditMode) {
            // Clear editing and selection when exiting edit mode
            layerOps.setEditingLayer(null);
            layerOps.setSelectedLayer(null);
        }
    }, [isEditMode]);

    // Notify when canvas is ready
    useEffect(() => {
        if (canvasRef.current && image) {  // Only notify when we have both canvas and image
            if (ref) {
                ref.current = canvasRef.current;
            }
            onCanvasReady?.();
        }
    }, [canvasRef.current, image, ref, onCanvasReady]);

    // Add auto-save trigger when layers change
    useEffect(() => {
        if (onContentChange) {
            onContentChange(layers);
        }
    }, [layers, onContentChange]);

    // Initialize layers with initial content if provided
    useEffect(() => {
        console.log('Initial content received:', initialContent);
        if (initialContent && layerOps.setLayers) {
            console.log('Setting layers to:', initialContent);
            layerOps.setLayers(initialContent);
        }
    }, [initialContent]);

    // Initialize image if provided
    useEffect(() => {
        console.log('Initial image received:', initialImage);
        if (initialImage) {
            console.log('Adding image layer');
            addImageLayer(initialImage);
        }
    }, [initialImage]);

    // Add new function to handle default map
    const handleUseDefaultMap = async () => {
        try {
            console.log('Starting to load default map');
            const img = new Image();
            
            img.onload = () => {
                console.log('Default map loaded successfully', {
                    width: img.width,
                    height: img.height,
                    src: img.src
                });
                handleImageUpload(null, img);  // Pass the already loaded image
            };
            
            img.onerror = (error) => {
                console.error('Error loading default map:', {
                    error,
                    src: img.src,
                    defaultMap
                });
            };
            
            console.log('Setting image src to:', defaultMap);
            img.src = defaultMap;
            
        } catch (error) {
            console.error('Error in handleUseDefaultMap:', error);
        }
    };

    // Render component
    return (
        <div style={{
            width,
            height,
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: isDragOver 
                ? '2px dashed #4a86e8' 
                : '2px solid #e0e0e0',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleImageUpload(e.target.files[0])}
                accept="image/png, image/jpeg"
                style={{ display: 'none' }}
            />
            
            {image ? (
                <>
                    <canvas
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'block',

                            cursor: isEditMode ? (
                                selectedTool === 'pointer' ? 'default' : 'crosshair'
                            ) : showResizeControls ? (
                                isMouseDown.current ? 'grabbing' : 'grab'
                            ) : 'default'
                        }}
                    />
                    
                    {isEditMode && (
                        <DrawingTools
                            selectedTool={selectedTool}
                            setSelectedTool={setSelectedTool}
                        />
                    )}

                    {showReuploadControls && (
                        <ReuploadControl
                            onReupload={() => fileInputRef.current.click()}
                        />
                    )}

                    {showResizeControls && (
                        <ResizeControls
                            onReset={handleReset}
                            onZoomIn={() => handleZoom('in')}
                            onZoomOut={() => handleZoom('out')}
                        />
                    )}

                    {isEditMode && (
                        <LayersPanel
                            layers={layers}
                            selectedLayer={selectedLayer}
                            isLayersPanelCollapsed={isLayersPanelCollapsed}
                            setIsLayersPanelCollapsed={layerOps.setIsLayersPanelCollapsed}
                            handleLayerClick={layerOps.handleLayerClick}
                            toggleLayerVisibility={layerOps.toggleLayerVisibility}
                            updateLayerColor={updateLayerColor}
                            deleteLayer={layerOps.deleteLayer}
                            moveLayer={layerOps.moveLayer}
                            onEditText={handleTextEdit}
                            onRename={layerOps.renameLayer}
                        />
                    )}

                    {editingText && canvasRef.current && (
                        <div
                            style={{
                                position: 'absolute',
                                left: '16px',
                                bottom: '16px',
                                width: '300px',
                                height: '150px',
                                zIndex: 1000,
                                background: 'white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '8px'
                            }}
                        >
                            <textarea
                                autoFocus
                                value={layers.find(l => l.id === editingText)?.data.content || ''}
                                onChange={(e) => handleTextChange(e, editingText)}
                                onBlur={() => setEditingText(null)}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    resize: 'none',
                                    fontFamily: 'Arial',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                    overflowY: 'auto'
                                }}
                            />
                        </div>
                    )}
                </>
            ) : (
                <UploadPrompt
                    onClick={() => fileInputRef.current.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    isDragOver={isDragOver}
                    onUseDefault={handleUseDefaultMap}
                />
            )}

            {showExportModal && (
                <ExportModal
                    onClose={() => setShowExportModal(false)}
                    onExport={onExport}
                    anonymousUserId={anonymousUserId}
                />
            )}
        </div>
    );
});

export default CanvasMap; 